import { DataSourceCodeConfig, DataSource, DataSourceIDEnum, DataSourceType } from '@/models/dataSource';
import { Form, Formik, FormikProps, getIn } from 'formik';
import { FC, SVGProps, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Input, VisualInput } from '@/components/inputs';
import { DataSourceFormModel } from '@/features/dataSourceEdit/types';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { prependHttp } from '@/utils';
import { RootState, useAppDispatch } from '@/reducers';
import { StepPageActions } from '@/global/components';
import { useSaveDataSourceMutation, useUpdateDataSourceMutation } from '@/services/dataSources';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useSelector } from 'react-redux';
import { DATA_SOURCE_ICONS } from '@/lib/Icons/DataSourceIcons/constants';

type SourceDetailsStepProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
};

export const SourceDetailsStep: FC<SourceDetailsStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { t } = useTranslation();

  const dataSourceTypeId = dataSourceType.id as DataSourceIDEnum;
  const RawSourceDetailsIcon = DATA_SOURCE_ICONS[dataSourceTypeId];
  const SourceDetailsIcon = (props: SVGProps<SVGSVGElement>) => (
    <RawSourceDetailsIcon className={props.className ?? 'size-[65px]'} />
  );
  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;

  const dataSourceInfoFormRef = useRef<FormikProps<DataSourceFormModel>>(null);

  const [saveDataSource, { isSuccess: isSaveSuccess, data: createdDataSource }] = useSaveDataSourceMutation();
  const [updateDataSource, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess, data: updatedDataSource }] = useUpdateDataSourceMutation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      url: Yup.string()
        .matches(
          /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm,
          t('data_source_edit:validations.not_valid_url')
        )
        .required(t('data_source_edit:validations.required')),
    }),
  });

  const handleNextStep = async () => {
    await dataSourceInfoFormRef.current?.submitForm();

    if (dataSourceInfoFormRef.current?.isValid && dataSourceInfoFormRef.current?.values) {
      createOrUpdateDataSource(dataSourceInfoFormRef.current?.values);
    }
  };

  const handleSubmit = async (form: DataSourceFormModel) => {
    if (form.name && form.configuration) {
      dispatch(
        stashDataSource({
          ...form,
          name: form.name,
          configuration: {
            ...form.configuration,
            url: prependHttp(form.configuration?.url),
          },
          type: dataSourceType,
        })
      );
    }
  };

  const createOrUpdateDataSource = async (dataSource: Partial<DataSource<DataSourceCodeConfig>>) => {
    if (!!dataSource.name && dataSource.configuration) {
      if (!dataSource.id) {
        await saveDataSource({
          workspaceId: workspace.id,
          dataSource: {
            type: dataSourceTypeId,
            name: dataSource.name,
            configuration: { ...dataSource.configuration, url: prependHttp(dataSource.configuration?.url) },
          },
        });
      } else {
        await updateDataSource({
          workspaceId: workspace.id,
          dataSourceId: dataSource.id,
          dataSource: {
            name: dataSource.name,
            configuration: { ...dataSource.configuration, url: prependHttp(dataSource.configuration?.url) },
          },
        });
      }
    }
  };

  useEffect(() => {
    if (createdDataSource) {
      dispatch(stashDataSource(createdDataSource.dataSource));
    }
  }, [createdDataSource]);

  useEffect(() => {
    if (updatedDataSource) {
      dispatch(stashDataSource(updatedDataSource));
    }
  }, [updatedDataSource]);

  useEffect(() => {
    if (isSaveSuccess) {
      onNextStep();
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isUpdateSuccess) {
      onNextStep();
    }
  }, [isUpdateSuccess]);

  return (
    <Formik
      innerRef={dataSourceInfoFormRef}
      initialValues={{ ...dataSource }}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ values, errors, handleChange, setFieldError }) => {
        return (
          <>
            <div className='flex grow gap-8'>
              <div className='flex grow flex-col gap-3'>
                <Form>
                  <VisualInput
                    name='type'
                    id='type'
                    label={t('data_source_edit:code.source_details.source_type')}
                    value={t(`data_sources:catalog.${dataSourceTypeId}.title`)}
                    icon={SourceDetailsIcon}
                  />
                  <Input
                    name='name'
                    id='name'
                    type='text'
                    label={t('data_source_edit:code.source_details.source_name')}
                    value={values.name}
                    placeholder={t('data_source_edit:code.source_details.source_name_placeholder')}
                    onChange={(e) => {
                      setFieldError('name', undefined);
                      handleChange('name')(e.target.value);
                    }}
                    error={errors.name}
                  />
                  <Input
                    name='configuration.url'
                    id='configuration.url'
                    type='text'
                    label={t('data_source_edit:code.source_details.source_website')}
                    value={values.configuration?.url}
                    placeholder={t('data_source_edit:code.source_details.source_website_placeholder')}
                    onChange={(e) => {
                      setFieldError('configuration.url', undefined);
                      handleChange('configuration.url')(e.target.value);
                    }}
                    error={getIn(errors.configuration, 'url')}
                  />
                </Form>
              </div>
            </div>

            <StepPageActions
              primaryButton={{
                text: 'COMMON:confirm',
                onClick: handleNextStep,
                isLoading: isSaveSuccess || isUpdateLoading,
              }}
              secondaryButton={{
                text: 'COMMON:cancel',
                onClick: onCancel,
              }}
            />
          </>
        );
      }}
    </Formik>
  );
};
